import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, useMatch, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { BookOpen, List } from 'lucide-react'
import { ButtonCreate } from '../../../components/styles/subheader-style'
import { Content } from './style'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { Header } from '../../../components/SubHeaderFilter/types'
import { PageHeader } from '../../../components/StandardPage'
import { FRETE_LABELS } from './Invoice/constants'
import { ViewModeSelector } from '../Pipelines/PipelineControls'
import { useSearch } from '../../../contexts/SearchContext'

export const invoiceHeaders: Header[] = [
    { key: 'numeroNF', label: 'NF', type: 'string' },
    { key: 'orderId', label: 'Pedido', type: 'number' },
    { key: 'chaveAcesso', label: 'Chave de acesso', type: 'string' },
    { key: 'client', label: 'Destinatário', type: 'string' },
    { key: 'cpfCnpj', label: 'CPF/CNPJ', type: 'string' },
    { key: 'inscricaoEstadual', label: 'Inscrição estadual', type: 'string' },
    { key: 'contribuinteIcms', label: 'Contribuinte ICMS', type: 'string' },
    { key: 'email', label: 'E-mail', type: 'string' },
    { key: 'address', label: 'Endereço', type: 'string' },
    { key: 'companyEmissor', label: 'Empresa emissora', type: 'string' },
    { key: 'naturezaOperacao', label: 'Natureza da operação', type: 'string', selectOptions: [
        { value: 'VENDA', label: 'Venda' },
        { value: 'DEVOLUCAO', label: 'Devolução' },
        { value: 'TRANSFERENCIA', label: 'Transferência' },
        { value: 'REMESSA', label: 'Remessa' },
        { value: 'RETORNO', label: 'Retorno' },
        { value: 'BONIFICACAO', label: 'Bonificação' },
        { value: 'EXPORTACAO', label: 'Exportação' },
        { value: 'IMPORTACAO', label: 'Importação' },
        { value: 'OUTRA', label: 'Outra' },
    ]},
    { key: 'tipoOperacao', label: 'Tipo de operação', type: 'string', selectOptions: [
        { value: 'ENTRADA', label: 'Entrada' },
        { value: 'SAIDA', label: 'Saída' },
    ]},
    { key: 'finalidade', label: 'Finalidade', type: 'string', selectOptions: [
        { value: 'NORMAL', label: 'Normal' },
        { value: 'COMPLEMENTAR', label: 'Complementar' },
        { value: 'AJUSTE', label: 'Ajuste' },
        { value: 'DEVOLUCAO', label: 'Devolução' },
    ]},
    { key: 'cfop', label: 'CFOP', type: 'string' },
    { key: 'items', label: 'Itens da nota', type: 'string' },
    { key: 'products', label: 'Produtos', type: 'string' },
    { key: 'payment', label: 'Pagamento', type: 'string' },
    { key: 'modalidadeFrete', label: 'Modalidade', type: 'string', selectOptions: Object.entries(FRETE_LABELS).map(([value, label]) => ({ value, label })) },
    { key: 'transportadora', label: 'Transportadora', type: 'string' },
    { key: 'dadosAdicionais', label: 'Dados adicionais', type: 'string' },
    { key: 'icms', label: 'ICMS', type: 'currency' },
    { key: 'ipi', label: 'IPI', type: 'currency' },
    { key: 'valorTotalNota', label: 'Valor total', type: 'currency' },
    { key: 'createdAt', label: 'Emissão', type: 'date' },
    { key: 'status', label: 'Status', type: 'string', selectOptions: [
        { value: 'RASCUNHO', label: 'Disponível' },
        { value: 'EMITINDO', label: 'Emitindo' },
        { value: 'EMITIDA', label: 'Emitida' },
        { value: 'NEGADA', label: 'Negada' },
        { value: 'CANCELADA', label: 'Cancelada' },
        { value: 'INUTILIZADA', label: 'Inutilizada' },
        { value: 'PENDENTE', label: 'Pendente' }
    ]}
]

export const ncmRulesHeaders: Header[] = [
    { key: 'capitulo', label: 'Capítulo', type: 'string' },
    { key: 'uso', label: 'Uso', type: 'string', selectOptions: [
        { value: 'com', label: 'Com uso' },
        { value: 'sem', label: 'Sem uso' },
    ]},
    { key: 'situacao', label: 'Situação', type: 'string', selectOptions: [
        { value: 'CONFIGURADA', label: 'Configurada' },
        { value: 'PARCIAL', label: 'Parcial' },
        { value: 'SEM_REGRA', label: 'Sem regra' },
    ]},
]

const ncmViewOptions = [
    { value: 'adotados', label: 'Adotados', icon: <List size={16} /> },
    { value: 'catalogo', label: 'Catálogo oficial', icon: <BookOpen size={16} /> },
]

export const taxScenarioHeaders: Header[] = [
    { key: 'nome', label: 'Nome', type: 'string' },
    { key: 'cnpjOrigem', label: 'CNPJ de origem', type: 'string' },
    { key: 'cfop', label: 'CFOP', type: 'string' },
    { key: 'ncms', label: 'NCMs', type: 'string' },
    { key: 'cstPIS_COFINS', label: 'CST PIS/COFINS', type: 'string' },
    { key: 'aliquotaPIS', label: 'Alíquota PIS%', type: 'number' },
    { key: 'aliquotaCOFINS', label: 'Alíquota COFINS%', type: 'number' },
    { key: 'cstIPI', label: 'CST IPI', type: 'string' },
    { key: 'cstICMS', label: 'CST ICMS', type: 'string' },
    { key: 'aliquotaICMS', label: 'Alíquota ICMS (%)', type: 'number' },
    { key: 'baseDeCalculo', label: 'Base de cálculo (%)', type: 'number' },
    { key: 'diferencialAliquota', label: 'Diferencial de alíquota', type: 'boolean' },
    { key: 'cstIBS_CBS', label: 'CST IBS/CBS', type: 'string' },
    { key: 'classificacaoTributaria', label: 'Classificação tributária (cClassTrib)', type: 'string' },
    { key: 'aliquotaCBS', label: 'Alíquota CBS (%)', type: 'number' },
    { key: 'aliquotaIBS', label: 'Alíquota IBS (%)', type: 'number' },
    { key: 'aplicarA', label: 'Aplicar a:', type: 'string' },
]


import { usePermissions } from '../../../hooks/usePermissions'

interface TypeMainOutlet {
    search: string
}

const taxTabs = [
    { path: '/panel/invoice', name: 'Nota Fiscal', permission: 'nota_fiscal' },
    { path: '/panel/invoice/tax-scenario', name: 'Cenário Fiscal', permission: 'cenario_fiscal' },
    { path: '/panel/invoice/tax-scenario/ncm-rules', name: 'Regras por NCM', permission: 'cenario_fiscal' },
    { path: '/panel/invoice/settings', name: 'Configurações', permission: 'nota_fiscal' }
]

const TaxModuleLayout: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission } = usePermissions()
    const [refresh, setRefresh] = useState(false)
    const [handleChildSubmit, setHandleChildSubmit] = useState<(() => void) | null>(null);
    const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
    const { search } = useOutletContext<TypeMainOutlet>() || { search: '' }

    const isTaxScenario = !!useMatch('/panel/invoice/tax-scenario')
    const isCreatingScenario = !!useMatch('/panel/invoice/tax-scenario/create')
    const isNcmRules = !!useMatch('/panel/invoice/tax-scenario/ncm-rules')
    const isSettings = !!useMatch('/panel/invoice/settings')
    const isCreatingInvoice = !!useMatch('/panel/invoice/create');
    const isEditingInvoice = !!useMatch('/panel/invoice/edit/:id');
    const isViewingInvoice = !!useMatch('/panel/invoice/view/:id');
    const isInvoiceDetail = isCreatingInvoice || isEditingInvoice || isViewingInvoice;

    const isInvoiceList = !isCreatingScenario && !isInvoiceDetail && !isTaxScenario && !isNcmRules && !isSettings

    const invoiceFiltersState = usePersistedFilters('tax_invoice')
    const scenarioFiltersState = usePersistedFilters('tax_scenario')
    const ncmFiltersState = usePersistedFilters('tax_ncm_rules')
    const { clearSearch } = useSearch()
    const [searchParams, setSearchParams] = useSearchParams()

    const isScenarioList = isTaxScenario && !isCreatingScenario
    const ncmView = searchParams.get('vista') === 'catalogo' ? 'catalogo' : 'adotados'
    const isNcmAdotados = isNcmRules && ncmView === 'adotados'

    const trocarVistaNcm = (vista: string) => {
        const next = new URLSearchParams(searchParams)
        if (vista === 'catalogo') next.set('vista', 'catalogo')
        else next.delete('vista')
        setSearchParams(next)
        clearSearch()
    }

    const { handleReplaceFilter: substituirFiltroNcm, isPrefsLoading: carregandoPrefsNcm } = ncmFiltersState
    // O chip de IPI do CreateScenario linka ncm-rules?semRegra=true: vira o filtro de situação do header.
    useEffect(() => {
        if (carregandoPrefsNcm || !isNcmRules || searchParams.get('semRegra') !== 'true') return
        substituirFiltroNcm({ key: 'situacao', label: 'Situação', filterType: 'Igual a', value: 'SEM_REGRA', displayValue: 'Sem regra', fieldType: 'string' })
        const next = new URLSearchParams(searchParams)
        next.delete('semRegra')
        setSearchParams(next, { replace: true })
    }, [carregandoPrefsNcm, isNcmRules, searchParams, setSearchParams, substituirFiltroNcm])

    const filtros = isInvoiceList ? invoiceFiltersState : isScenarioList ? scenarioFiltersState : isNcmAdotados ? ncmFiltersState : null
    const filterHeaders = isInvoiceList ? invoiceHeaders : isScenarioList ? taxScenarioHeaders : isNcmAdotados ? ncmRulesHeaders : undefined

    const activeTabKey = isInvoiceDetail || isCreatingScenario
        ? (isCreatingScenario ? '/panel/invoice/tax-scenario' : '/panel/invoice')
        : location.pathname

    const visibleTabs = taxTabs.filter((t) => hasPermission(t.permission))

    return (
        <>
            <PageHeader
                title='Fiscal'
                tableName={isScenarioList ? 'TaxConfig' : isNcmRules ? 'TaxNcmProfile' : isSettings ? 'CompanyGateway' : 'Invoice'}
                moduleName={isScenarioList ? 'Cenários Fiscais' : isNcmRules ? 'Regras por NCM' : isSettings ? 'Configurações fiscais' : 'Notas Fiscais'}
                tabs={visibleTabs.map((tab) => ({ key: tab.path, label: tab.name }))}
                activeTab={activeTabKey}
                hasTable={isInvoiceList || isScenarioList}
                showAuditHeaderMenu={isInvoiceList || isScenarioList}
                onTabChange={(key) => { clearSearch(); navigate(key) }}
                onReload={!isCreatingScenario ? () => setRefresh((prev) => !prev) : undefined}
                filterHeaders={filterHeaders}
                appliedFilters={filtros?.appliedFilters}
                onApplyFilter={filtros?.handleApplyFilter}
                onRemoveFilter={filtros?.handleRemoveFilter}
                onClearAllFilters={filtros?.handleClearAll}
                rightExtra={isNcmRules ? (
                    <ViewModeSelector value={ncmView} options={ncmViewOptions} onChange={trocarVistaNcm} />
                ) : undefined}
                extraActions={isCreatingScenario ? (
                    <>
                        <ButtonCreate
                            onClick={() => navigate(-1)}
                            style={{ background: '#fff', color: '#0090D3', border: '1px solid #0090D3' }}
                        >
                            Cancelar
                        </ButtonCreate>
                        <ButtonCreate onClick={() => handleChildSubmit && handleChildSubmit()}>
                            Salvar Cenário
                        </ButtonCreate>
                    </>
                ) : (isInvoiceDetail || isSettings) ? headerActions : undefined}
                createButton={!isCreatingScenario && isTaxScenario ? {
                    label: 'Novo Cenário',
                    onClick: () => navigate('/panel/invoice/tax-scenario/create'),
                } : isInvoiceList ? {
                    label: 'Nova Nota Fiscal',
                    onClick: () => navigate('/panel/invoice/create'),
                } : isNcmAdotados ? {
                    label: 'Adicionar NCM',
                    onClick: () => trocarVistaNcm('catalogo'),
                } : isSettings && !headerActions ? {
                    label: 'Novo emissor',
                    onClick: () => navigate('/panel/invoice/settings?novo=1'),
                } : undefined}
            />

            <Content>
                <Outlet context={{ setHandleChildSubmit, setHeaderActions, refresh, search, appliedFilters: invoiceFiltersState.appliedFilters, scenarioFilters: scenarioFiltersState.appliedFilters, ncmFilters: ncmFiltersState.appliedFilters }} />
            </Content>
        </>
    )
}

export default TaxModuleLayout
